import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

/** Minimal announcement shape for create/edit. */
export interface AnnouncementRow {
  id: string
  vendor_id: string | null
  announcement_text: string
  expiry_days: number
  image_url: string | null
  created_at?: string
  expires_at?: string
}

interface CreateAnnouncementModalProps {
  /** Vendor ID for vendor announcements (broadcasters/ad-creators). Null for platform announcements (admins). */
  vendorId: string | null
  onClose: () => void
  onSuccess: () => void
  /** When set, modal is in edit mode: prefill form and update this announcement. */
  existing?: AnnouncementRow | null
}

export default function CreateAnnouncementModal({
  vendorId,
  onClose,
  onSuccess,
  existing = null,
}: CreateAnnouncementModalProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isEditMode = !!existing

  const [announcementText, setAnnouncementText] = useState(existing?.announcement_text ?? '')
  const [expiryDays, setExpiryDays] = useState(existing ? String(existing.expiry_days) : '1')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [clearImage, setClearImage] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (existing) {
      setAnnouncementText(existing.announcement_text)
      setExpiryDays(String(existing.expiry_days))
      setImagePreview(existing.image_url && !clearImage ? existing.image_url : null)
    }
  }, [existing, clearImage])

  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
  const MAX_IMAGE_SIZE_MB = 5

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose, isSaving])

  const uploadAnnouncementImage = async (file: File): Promise<string | null> => {
    if (!user) return null
    const fileExt = file.name.split('.').pop() || 'jpg'
    const fileName = `${user.id}-${Date.now()}.${fileExt}`
    const filePath = `announcements/${fileName}`

    const { error: uploadError } = await supabase.storage
      .from('announcement-images')
      .upload(filePath, file, { upsert: true })

    if (uploadError) {
      console.error('Error uploading announcement image:', uploadError)
      throw new Error(`Failed to upload image: ${uploadError.message}`)
    }

    const { data } = supabase.storage
      .from('announcement-images')
      .getPublicUrl(filePath)

    return data.publicUrl
  }

  const saveAnnouncementMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated')
      if (!announcementText.trim()) {
        throw new Error('Announcement text is required')
      }

      const expiryDaysNum = parseInt(expiryDays, 10)
      if (isNaN(expiryDaysNum) || expiryDaysNum < 1) {
        throw new Error('Expiry days must be a positive number')
      }

      let imageUrl: string | null
      if (imageFile) {
        imageUrl = await uploadAnnouncementImage(imageFile)
      } else if (isEditMode && clearImage) {
        imageUrl = null
      } else if (isEditMode && existing?.image_url) {
        imageUrl = existing.image_url
      } else {
        imageUrl = null
      }

      if (isEditMode && existing) {
        const { error } = await supabase
          .from('announcements')
          .update({
            announcement_text: announcementText.trim(),
            expiry_days: expiryDaysNum,
            image_url: imageUrl,
          })
          .eq('id', existing.id)

        if (error) {
          throw new Error(`Failed to update announcement: ${error.message}`)
        }
      } else {
        const { error } = await supabase
          .from('announcements')
          .insert({
            vendor_id: vendorId,
            announcement_text: announcementText.trim(),
            expiry_days: expiryDaysNum,
            image_url: imageUrl,
          })

        if (error) {
          throw new Error(`Failed to create announcement: ${error.message}`)
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
      queryClient.invalidateQueries({ queryKey: ['my-announcements'] })
      queryClient.invalidateQueries({ queryKey: ['admin-announcements'] })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      console.error('Error saving announcement:', error)
      alert(`Failed to ${isEditMode ? 'update' : 'create'} announcement: ${error.message}`)
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSaving) return

    setIsSaving(true)
    try {
      await saveAnnouncementMutation.mutateAsync()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSaving) {
          onClose()
        }
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          border: '2px solid var(--bg-tertiary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2
            style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
            }}
          >
            {isEditMode ? 'Edit Announcement' : 'Create Announcement'}
          </h2>
          <button
            onClick={onClose}
            disabled={isSaving}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              color: 'var(--text-primary)',
              padding: '0.25rem 0.5rem',
            }}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="announcement-text"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Announcement Text *
            </label>
            <textarea
              id="announcement-text"
              value={announcementText}
              onChange={(e) => setAnnouncementText(e.target.value)}
              required
              rows={6}
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '0.5rem',
                border: '2px solid var(--bg-tertiary)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '0.95rem',
                resize: 'vertical',
              }}
              placeholder="Enter your announcement text here..."
            />
          </div>

          <div className="mb-4">
            <label
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Image (optional)
            </label>
            <input
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(',')}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (!file) {
                  setImageFile(null)
                  setImagePreview(null)
                  setClearImage(false)
                  return
                }
                if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
                  alert(`Image must be under ${MAX_IMAGE_SIZE_MB} MB`)
                  e.target.value = ''
                  return
                }
                if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
                  alert('Please choose a JPEG, PNG, WebP or GIF image.')
                  e.target.value = ''
                  return
                }
                setImageFile(file)
                setClearImage(false)
                const reader = new FileReader()
                reader.onloadend = () => setImagePreview(reader.result as string)
                reader.readAsDataURL(file)
              }}
              className="block w-full text-sm"
              style={{ color: 'var(--text-primary)' }}
              aria-describedby="image-upload-hint"
            />
            <p
              id="image-upload-hint"
              className="text-sm mt-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              JPEG, PNG, WebP or GIF. Max {MAX_IMAGE_SIZE_MB} MB. Shown on the announcement card.
            </p>
            {(imagePreview || (isEditMode && existing?.image_url && !clearImage && !imageFile)) && (
              <div className="mt-2 flex items-start gap-2">
                <img
                  src={imagePreview || (existing?.image_url ?? '')}
                  alt="Preview"
                  style={{
                    maxWidth: '160px',
                    maxHeight: '120px',
                    objectFit: 'contain',
                    borderRadius: '0.5rem',
                    border: '2px solid var(--bg-tertiary)',
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setImageFile(null)
                    setImagePreview(null)
                    setClearImage(true)
                  }}
                  className="text-sm font-medium"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Remove image
                </button>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label
              htmlFor="expiry-days"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Expiry (days) *
            </label>
            <input
              type="number"
              id="expiry-days"
              value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value)}
              required
              min="1"
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '0.5rem',
                border: '2px solid var(--bg-tertiary)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '0.95rem',
              }}
            />
            <p
              style={{
                fontSize: '0.85rem',
                color: 'var(--text-secondary)',
                marginTop: '0.25rem',
              }}
            >
              The announcement will automatically expire after this many days.
            </p>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-6 py-2 font-semibold rounded"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                opacity: isSaving ? 0.5 : 1,
                cursor: isSaving ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !announcementText.trim()}
              className="px-6 py-2 font-semibold rounded"
              style={{
                backgroundColor: 'var(--bg-interactive)',
                color: 'var(--text-inverse)',
                opacity: isSaving || !announcementText.trim() ? 0.5 : 1,
                cursor: isSaving || !announcementText.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              {isSaving ? (isEditMode ? 'Saving...' : 'Creating...') : isEditMode ? 'Save changes' : 'Create Announcement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

